import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from './api.service';

export interface CalendarState {
  id: number;
  ref: string;
  code: string;
}

export interface CalendarCountry {
  id: number;
  iso: string;
  name: string;
  states?: CalendarState[];
}

export interface CalendarHoliday {
  type: number;
  month: number;
  day: number;
  showName: boolean;
  name: string;
}

interface CountriesResponse {
  countries: Array<{
    country_id: number;
    iso: string;
    name: string;
    states?: Array<{ state_id: number; ref: string; code: string }>;
  }>;
}

interface HolidaysResponse {
  holidays: CalendarHoliday[];
}

// Backend stores names uppercased (e.g. "REINO UNIDO"); UI wants natural case.
// `\p{L}` covers accented letters too — "ESPAÑA" → "España".
function toTitleCase(input: string): string {
  return input.toLowerCase().replace(/(^|\s)(\p{L})/gu, (_, sep, ch) => sep + ch.toUpperCase());
}

@Injectable({ providedIn: 'root' })
export class CalendarsApiService {
  private readonly api = inject(ApiService);

  getCountries(): Observable<CalendarCountry[]> {
    return this.api.get<CountriesResponse>('/calendars/countries').pipe(
      map(res => (res?.countries ?? []).map(c => ({
        id: c.country_id,
        iso: c.iso,
        name: toTitleCase(c.name),
        states: c.states?.map(s => ({ id: s.state_id, ref: toTitleCase(s.ref), code: s.code }))
      })))
    );
  }

  getHolidays(countryId: number, year: number, stateId?: number | null): Observable<CalendarHoliday[]> {
    const params: Record<string, string | number> = {};
    if (stateId != null && stateId > 0) params['state'] = stateId;
    return this.api.get<HolidaysResponse>(
      `/calendars/countries/${countryId}/holidays/${year}`,
      Object.keys(params).length ? { params } : undefined
    ).pipe(map(res => res?.holidays ?? []));
  }
}
